import {Component, OnInit} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import Env from '../../../env/env';
import {ArtistResult, ArtworkData, ArtworkDocument, getName, SegmentationResult} from '../../elastic-utils';

@Component({
  selector: 'app-search-artworks',
  templateUrl: './search-artworks.component.html',
  styleUrls: ['./search-artworks.component.css']
})
export class SearchArtworksComponent implements OnInit {
  private readonly getName = getName;
  private readonly imageCDN = Env.imageCDN;
  private readonly getKeys = Object.keys;
  private readonly getEntries = Object.entries;
  private readonly getValues = Object.values;

  result: ArtistResult;
  error: any;
  aphc: string;
  unique = false;

  searchMode = true;
  autoSegment = false;

  private artworkData: ArtworkData = {
    artwork_name: '',
    artwork_date: '',
    date_on_artwork: 0,
    signature_on_artwork: 0,
    provenance: '',
    exhibited: '',
    literature: '',
    unit: 'mm',
    d1: null,
    d2: null,
    d3: null,
    mediums_analysis: '',
    mediums: null,
    medium_v2: null,
    medium_v2_analysis: '',
    edition_analysis: '',
    markings: '',
    artistId: 0,
    segmentation_price: 1,
    segmentation_price_v2: 1
  };

  private readonly mediums = {
    'Work On Paper': 1,
    'Video': 2,
    'Textile': 3,
    'Sculpture': 4,
    'Photograph': 5,
    'Painting': 6,
    'Multimedia': 7,
    'Mixed Media': 8,
    'Installation': 9,
    'Engraving': 10,
    'Decorative Art': 11,
  };

  private readonly mediumsV2 = {
    "Work On Paper": 1,
    "Volume 1": 2,
    "Volume 2": 3,
    "Photograph 1": 4,
    "Photograph 2": 5,
    "Photograph 3": 6,
    "Photograph 4": 7,
    "Photograph 5": 8,
    "Photograph 6": 9,
    "Painting": 10,
    "Mixed Media": 11,
    "Engraving 1": 12,
    "Engraving 2": 13,
    "Engraving 3": 14,
    "Engraving 4": 15,
    "Digital Art": 16,
    "Decorative Art": 17,
  }

  constructor(private http: HttpClient, private router: Router, private route: ActivatedRoute) {
  }

  ngOnInit() {
    this.artworkData.artistId = parseInt(this.route.snapshot.paramMap.get('artistId'), 10);

    let relatedArtwork: ArtworkDocument = null;
    if (this.route.snapshot.fragment) {
      relatedArtwork = JSON.parse(unescape(atob(this.route.snapshot.fragment)));
      this.loadArtwork(relatedArtwork)
    }

    this.http.get<ArtistResult>(Env.apiUrl + '/artists/' + this.artworkData.artistId).subscribe((res) => {
      this.result = res;
    }, err => {
      this.error = err;
    });
  }

  isSelectedMedium(a, b) {
    return a === b;
  }

  checkMedium(e, medium) {
    const mediumId = this.mediumsV2[medium];
    e.preventDefault();
    if (medium === this.artworkData.medium_v2) {
      this.artworkData.medium_v2 = null;
      this.artworkData.medium_v2_analysis = null;
    } else {
      this.artworkData.medium_v2 = mediumId;
      this.artworkData.medium_v2_analysis = medium;
    }
    console.log(medium, mediumId, this.artworkData.medium_v2);
    this.refresh();
  }

  refresh() {
    if (this.searchMode) {
      this.search();
    } else if (this.autoSegment) {
      this.updateSegment();
    }
  }

  updateSegment() {
    console.log('Segment Debug', this.artworkData)
    let params = new HttpParams();
    if (this.artworkData.artwork_name) {
      params = params.append('name', this.artworkData.artwork_name);
    }
    if (this.artworkData.artwork_date) {
      params = params.append('date', this.artworkData.artwork_date);
    }
    if (this.artworkData.d1) {
      params = params.append('d1', this.artworkData.d1.toString());
    }
    if (this.artworkData.d2) {
      params = params.append('d2', this.artworkData.d2.toString());
    }
    if (this.artworkData.d3) {
      params = params.append('d3', this.artworkData.d3.toString());
    }
    if (this.artworkData.medium_v2) {
      params = params.append('mediumId', this.artworkData.medium_v2.toString());
    }

    this.http.get<SegmentationResult>(Env.apiUrl + '/segmentation/' + this.artworkData.artistId, {params}).subscribe(res => {
      console.log(res);
      this.artworkData.segmentation_price_v2 = res.segmentationPrice;
    });
  }

  search() {
    if (this.artworkData.artwork_name) {
      let params = new HttpParams();
      params = params.append('term', this.artworkData.artwork_name);

      if (this.artworkData.artwork_date) {
        params = params.append('date', this.artworkData.artwork_date);
      }
      if (this.artworkData.medium_v2) {
        params = params.append('mediumId', this.artworkData.medium_v2.toString());
      }

      this.http.get<ArtworkDocument[]>(
        Env.apiUrl + '/artists/' + this.artworkData.artistId + '/artworks/search', {params}).subscribe((data) => {
        this.result.artworks = data;
      });
    } else {
      this.http.get<ArtistResult>(Env.apiUrl + '/artists/' + this.artworkData.artistId).subscribe((res) => {
        this.result = res;
      }, err => {
        this.error = err;
      });
    }
  }

  getImageUrl(path: string) {
    const artworkUrl = path.startsWith('http') ? encodeURIComponent(path) : encodeURIComponent(Env.imageCDN + path);
    return 'https://images.weserv.nl/?blur=0&h=250&url=' + artworkUrl;
  }

  loadArtwork(artwork: ArtworkDocument) {
    this.searchMode = false;
    this.autoSegment = false;
    this.http.get<ArtworkData>(Env.apiUrl + '/artwork/' + artwork._source.id).subscribe((res) => {
      res.artistId = this.artworkData.artistId;
      this.artworkData = res;
      this.artworkData.picture_url = artwork._source.pictureUrl;
      if (this.artworkData.segmentation_price === 'na') {
        this.artworkData.segmentation_price = 3;
      }
      this.searchMode = false;
    });
  }

  create() {
    this.searchMode = false;
    this.autoSegment = true;
  }

  estimate() {
    if (this.artworkData.d1 === null) {
      this.artworkData.d1 = 0;
    } else if (typeof this.artworkData.d1 === 'string') {
      this.artworkData.d1 = parseInt(this.artworkData.d1 as string, 10);
    }

    if (this.artworkData.d2 === null) {
      this.artworkData.d2 = 0;
    } else if (typeof this.artworkData.d2 === 'string') {
      this.artworkData.d2 = parseInt(this.artworkData.d2 as string, 10);
    }

    if (this.artworkData.d3 === null) {
      this.artworkData.d3 = 0;
    } else if (typeof this.artworkData.d3 === 'string') {
      this.artworkData.d3 = parseInt(this.artworkData.d3 as string, 10);
    }

    // @ts-ignore
    this.artworkData.artwork_date = parseInt(this.artworkData.artwork_date, 10);

    this.artworkData.signature_on_artwork = this.artworkData.signature_on_artwork ? 1 : 0;
    this.artworkData.date_on_artwork = this.artworkData.date_on_artwork ? 1 : 0;

    if (this.aphc)
      this.artworkData.edition_analysis += ' ' + this.aphc;

    this.router.navigate(['/estimate'], {
      fragment: btoa(escape(JSON.stringify(this.artworkData)))
    });
  }
}
